import { useRef, useState } from 'react';
import { Plus, Pencil, Trash2, X, Check, ImagePlus, ArrowUpDown, FolderTree } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { ReorderListDialog } from '@/components/pos-management/reorder-list-dialog';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useCategories, useCreateCategory, useUpdateCategory, useDeleteCategory } from '@/hooks/use-categories';
import { useUploadCategoryImage } from '@/hooks/use-category-department-image';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';
import { toast } from '@/stores/toast-store';
import { normalizeCatalogName } from '@/lib/product-xlsx';

/** Thumbnail + upload button for a category's POS tile image. */
function CategoryImageCell({ cat, readOnly }: { cat: any; readOnly: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadCategoryImage();
  const uploadEnabled = useAuthStore((s) => s.tenant?.allowCategoryDepartmentImageUpload === true);
  // Super-admin gated. When off, show an existing image read-only (no upload
  // control) and hide the cell entirely when there's nothing to show.
  if (!uploadEnabled || readOnly) {
    return cat.imageUrl ? (
      <img src={cat.imageUrl} alt="" className="h-9 w-9 rounded-md border border-border object-cover" />
    ) : null;
  }
  return (
    <div className="flex items-center gap-2">
      {cat.imageUrl ? (
        <img src={cat.imageUrl} alt="" className="h-9 w-9 rounded-md border border-border object-cover" />
      ) : (
        <span className="flex h-9 w-9 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
          <ImagePlus className="h-4 w-4" />
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          try {
            await upload.mutateAsync({ categoryId: cat.id, file });
            toast.success('Image uploaded — POS updates on next sync');
          } catch {
            toast.error('Image upload failed');
          }
        }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        className="text-xs text-primary hover:underline disabled:opacity-50"
      >
        {upload.isPending ? 'Uploading…' : cat.imageUrl ? 'Replace' : 'Upload'}
      </button>
    </div>
  );
}

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const CATEGORY_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1', '#14b8a6'];

export function CategoryListPage() {
  const { data, isLoading } = useCategories();
  const permissions = usePermissions();
  const canAdd = permissions.has('categories.add');
  const canEdit = permissions.has('categories.edit');
  const canDelete = permissions.has('categories.delete');
  const allCategories = data?.data ?? [];

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  // POS display order (sort_order) first, then alphabetical.
  const sortedCategories = [...allCategories].sort(
    (a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name ?? '').localeCompare(b.name ?? ''),
  );
  const total = sortedCategories.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const paginatedCategories = sortedCategories.slice((page - 1) * perPage, page * perPage);

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [showCreate, setShowCreate] = useState(false);
  const [showReorder, setShowReorder] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  // Persist the dragged order: each category's sort_order = its new index.
  const handleSaveOrder = async (orderedIds: string[]) => {
    setSavingOrder(true);
    try {
      await Promise.all(orderedIds.map((id, i) => updateCategory.mutateAsync({ id, sortOrder: i })));
      toast.success('Category order saved \u2014 POS devices update on next sync');
      setShowReorder(false);
    } catch {
      toast.error('Failed to save category order');
    } finally {
      setSavingOrder(false);
    }
  };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState(CATEGORY_COLORS[0]);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string; linkedCount: number } | null>(null);

  const handleCreate = async () => {
    if (!normalizeCatalogName(formName)) return;
    await createCategory.mutateAsync({
      name: normalizeCatalogName(formName),
      color: formColor,
    });
    setFormName('');
    setFormColor(CATEGORY_COLORS[0]);
    setShowCreate(false);
  };

  const startEdit = (cat: any) => {
    setEditingId(cat.id);
    setFormName(cat.name);
    setFormColor(cat.color || CATEGORY_COLORS[0]);
  };

  const handleUpdate = async () => {
    if (!normalizeCatalogName(formName) || !editingId) return;
    await updateCategory.mutateAsync({
      id: editingId,
      name: normalizeCatalogName(formName),
      color: formColor,
    });
    setEditingId(null);
    setFormName('');
    setFormColor(CATEGORY_COLORS[0]);
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await deleteCategory.mutateAsync({ id });
      toast.success(`Deleted category "${name}"`);
    } catch (e: any) {
      if (e?.error?.code === 'CATEGORY_HAS_PRODUCTS') {
        setDeleteTarget({ id, name, linkedCount: e.error.linkedProductCount ?? 0 });
        return;
      }
      toast.error(e?.error?.message || `Failed to delete "${name}"`);
    }
  };

  const handleConfirmCascadeDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCategory.mutateAsync({ id: deleteTarget.id, cascade: true });
      toast.success(
        `Deleted "${deleteTarget.name}" and unassigned ${deleteTarget.linkedCount} product${
          deleteTarget.linkedCount === 1 ? '' : 's'
        }`,
      );
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.error?.message || 'Failed to delete category');
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Categories">
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              onClick={() => setShowReorder(true)}
              disabled={sortedCategories.length < 2}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50"
            >
              <ArrowUpDown className="h-4 w-4" /> Reorder
            </button>
          )}
          {canAdd && (
            <button
              onClick={() => {
                setShowCreate(true);
                setFormName('');
                setFormColor(CATEGORY_COLORS[0]);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Create
            </button>
          )}
        </div>
      </PageHeader>

      <ReorderListDialog
        open={showReorder}
        onClose={() => setShowReorder(false)}
        title="Reorder categories"
        items={sortedCategories.map((c: any) => ({ id: c.id, name: c.name }))}
        onSave={handleSaveOrder}
        saving={savingOrder}
      />

      <div className="rounded-lg border border-border bg-white">
        {showCreate && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3 bg-muted/30">
            <input
              type="text"
              placeholder="Category Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              autoFocus
              className="h-9 min-w-0 flex-1 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <div className="flex flex-wrap items-center gap-1">
              {CATEGORY_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormColor(color)}
                  className={`h-7 w-7 shrink-0 rounded-full border-2 ${formColor === color ? 'border-foreground' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <button
              onClick={handleCreate}
              disabled={createCategory.isPending}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">Loading...</div>
        ) : allCategories.length === 0 ? (
          <EmptyState
            icon={FolderTree}
            title="No categories yet"
            description="Click Create to add your first category."
          />
        ) : (
          <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[320px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className="px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase">
                    Color
                  </th>
                  <th className="px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase">
                    Name
                  </th>
                  <th className="px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase">
                    Image
                  </th>
                  <th className="px-5 py-3.5 text-right text-[13px] font-semibold tracking-wide text-gray-700 uppercase w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedCategories.map((cat: any) => (
                  <tr key={cat.id} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                    {editingId === cat.id ? (
                      <>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap items-center gap-1">
                            {CATEGORY_COLORS.map((color) => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => setFormColor(color)}
                                className={`h-6 w-6 shrink-0 rounded-full border-2 ${formColor === color ? 'border-foreground' : 'border-transparent'}`}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdate()}
                            autoFocus
                            className="h-8 w-full rounded border border-input px-2 text-sm outline-none focus:border-primary"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <CategoryImageCell cat={cat} readOnly={!canEdit} />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={handleUpdate}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <span
                            className="inline-block h-5 w-5 rounded-full"
                            style={{ backgroundColor: cat.color || '#6b7280' }}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">{cat.name}</td>
                        <td className="px-4 py-3">
                          <CategoryImageCell cat={cat} readOnly={!canEdit} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canEdit && (
                              <button
                                onClick={() => startEdit(cat)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                                title="Edit category"
                                aria-label="Edit category"
                              >
                                <Pencil className="h-4 w-4 text-primary" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(cat.id, cat.name)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-red-50"
                                title="Delete category"
                                aria-label="Delete category"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!isLoading && allCategories.length > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={handlePerPageChange}
        />
      )}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Unassign products and delete "${deleteTarget?.name}"?`}
        message={
          deleteTarget
            ? `${deleteTarget.linkedCount} product${deleteTarget.linkedCount === 1 ? ' is' : 's are'} still linked to this category. Continuing will remove this category from ${deleteTarget.linkedCount === 1 ? 'that product' : 'those products'} and then delete the category. This cannot be undone.`
            : ''
        }
        confirmLabel={`Unassign & delete`}
        variant="danger"
        loading={deleteCategory.isPending}
        onConfirm={handleConfirmCascadeDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
